import { create } from 'zustand';

const useStore = create((set, get) => ({
  // Gate auth state (password protection)
  isGateAuthenticated: false,

  // Demo auth state (user selection)
  user: null,
  isAuthenticated: false,
  selectedTenant: null,

  // UI state
  sidebarOpen: false,
  aiAssistantOpen: false,
  pendingAssistantMessage: null, // Message to auto-send when assistant opens
  selectedAsset: null,
  activeView: 'fleet',

  // Fleet data
  assets: [],
  partsInTransit: [],
  fleetSummary: null,

  // Jobs data
  jobs: [],
  jobStats: null,

  // Incidents data
  incidents: [],
  incidentStats: null,

  // WebSocket connection
  wsConnected: false,

  // Actions - Gate Auth
  setGateAuthenticated: (authenticated) => set({ isGateAuthenticated: authenticated }),

  gateLogout: () => set({
    isGateAuthenticated: false,
    user: null,
    isAuthenticated: false,
    selectedTenant: null
  }),

  // Actions - Demo Auth
  login: (user, tenant) => set({
    user,
    isAuthenticated: true,
    selectedTenant: tenant
  }),

  logout: () => set({
    user: null,
    isAuthenticated: false,
    selectedTenant: null
  }),

  switchPersona: (user) => set({ user }),

  // Actions - UI
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

  toggleAiAssistant: () => set((state) => ({ aiAssistantOpen: !state.aiAssistantOpen })),

  sendToAssistant: (message) => set({
    pendingAssistantMessage: message,
    aiAssistantOpen: true
  }),

  clearPendingMessage: () => set({ pendingAssistantMessage: null }),

  setSelectedAsset: (asset) => set({ selectedAsset: asset }),

  setActiveView: (view) => set({ activeView: view }),

  // Actions - Data
  setAssets: (assets) => set({ assets }),

  setPartsInTransit: (parts) => set({ partsInTransit: parts }),

  setFleetSummary: (summary) => set({ fleetSummary: summary }),

  setJobs: (jobs) => set({ jobs }),

  setJobStats: (stats) => set({ jobStats: stats }),

  updateJobStatus: (jobId, status) => set((state) => ({
    jobs: state.jobs.map(job =>
      job.id === jobId ? { ...job, status } : job
    )
  })),

  setIncidents: (incidents) => set({ incidents }),

  setIncidentStats: (stats) => set({ incidentStats: stats }),

  // Actions - WebSocket
  setWsConnected: (connected) => set({ wsConnected: connected }),

  // Handle real-time updates
  handleRealtimeUpdate: (update) => {
    const state = get();

    switch (update.type) {
      case 'telemetry_update':
        // Update asset metrics
        set({
          assets: state.assets.map(asset =>
            asset.id === update.assetId
              ? { ...asset, metrics: { ...asset.metrics, ...update.metrics }, lastUpdate: update.timestamp }
              : asset
          )
        });
        break;

      case 'asset_status_change':
        set({
          assets: state.assets.map(asset =>
            asset.id === update.assetId
              ? { ...asset, status: update.newStatus, riskScore: update.riskScore }
              : asset
          )
        });
        break;

      case 'alert_triggered':
        set({
          assets: state.assets.map(asset =>
            asset.id === update.assetId
              ? { ...asset, alerts: [...(asset.alerts || []), update.alert] }
              : asset
          )
        });
        break;

      case 'job_update':
        set({
          jobs: state.jobs.map(job =>
            job.id === update.jobId
              ? { ...job, status: update.status, progress: update.progress }
              : job
          )
        });
        break;

      case 'parts_location_update':
        set({
          partsInTransit: state.partsInTransit.map(part =>
            part.id === update.partId
              ? { ...part, currentPosition: update.currentPosition, progress: update.progress, eta: update.eta }
              : part
          )
        });
        break;

      default:
        console.log('Unknown update type:', update.type);
    }
  }
}));

export default useStore;
